import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import ReactDOM from 'react-dom';
import { interactiveFetch } from '@/api';
import { Filter, X, Loader2, Search, ChevronDown, Check, CheckSquare, Settings, ToggleLeft, Trash2 } from 'lucide-react';
import { ConfirmModal } from '@/components/common/ConfirmModal';

import { queryBatcher } from '@/hooks/DashboardQueryBatcher';

// ==================== DROPDOWN FILTER ====================
const DropdownFilter = ({
    fieldName,
    dataset,
    dataModel,
    selectedValue,
    selectedValues = [],
    onFilterChange,
    searchEnabled = true,
    selectionMode = 'single',
    width = 0,
    height = 32,
    fontSize = 10,
    dashboardId,
    widgetId,
    useLatest = false,
    sortBy = null,
    isDate = false,
    dateFormat = null,
    onFiltersResolved = null,
    label = fieldName,
    isEditMode = false,
}) => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
    const triggerRef = useRef(null);

    const updatePosition = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setDropdownPos({
                top: rect.bottom + window.scrollY + 4,
                left: rect.left + window.scrollX,
                width: Math.max(rect.width, 200)
            });
        }
    };

    useEffect(() => {
        if (isOpen) {
            updatePosition();
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);
            return () => {
                window.removeEventListener('scroll', updatePosition, true);
                window.removeEventListener('resize', updatePosition);
            };
        }
    }, [isOpen]);

    useEffect(() => {
        if (!fieldName || (!dataset?.id && !dataModel?.id)) { setOptions([]); return; }

        const fetchDistinct = async () => {
            setLoading(true);
            try {
                const ySeries = [{ id: 'cnt', field: fieldName, agg: 'count', label: 'Count' }];
                let sortTarget = 'cnt';

                if (useLatest) {
                    if (sortBy && sortBy !== fieldName) {
                        ySeries.push({ id: 'sort_val', field: sortBy, agg: 'max', label: 'Sort Val' });
                        sortTarget = 'sort_val';
                    } else {
                        sortTarget = fieldName;
                    }
                }

                const payload = {
                    dataset_id: dataModel?.id ? undefined : (dataset?.id || undefined),
                    data_model_id: dataModel?.id || undefined,
                    dashboard_id: dashboardId,
                    limit: 500,
                    x_field: fieldName,
                    y_series: ySeries,
                    columns: null, filters: [], sort_by: sortTarget, sort_dir: 'desc',
                    group_field: null, rank_by: null
                };
                
                const uniqueWidgetId = widgetId ? `${widgetId}_filter_${fieldName}` : null;
                const result = await queryBatcher.fetch(dashboardId, uniqueWidgetId, payload);
                if (result?.data && Array.isArray(result.data)) {
                    const values = result.data.map(row => row[fieldName] || row.x_val).filter(v => v != null);
                    let uniqueValues = [...new Set(values)];


                    // --- DATE SORTING (Descending by actual date, not lexicographic) ---
                    if (isDate) {
                        // Custom parser that understands common format hints like "MMM YYYY"
                        const MONTH_MAP: Record<string, number> = {
                            jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
                            jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
                            january: 0, february: 1, march: 2, april: 3, june: 5,
                            july: 6, august: 7, september: 8, october: 9, november: 10, december: 11
                        };

                        const parseDate = (val: string): number => {
                            const s = String(val).trim();
                            const fmt = (dateFormat || '').toUpperCase();

                            // Format: "MMM YYYY" or "MMMM YYYY" — e.g. "Mar 2024", "March 2024", or compact "Mar2024"
                            if (fmt.includes('MMM') && fmt.includes('YYYY')) {
                                // Match optional space between month and year: "Mar2024" or "Mar 2024"
                                const m = s.match(/^([A-Za-z]+)\s*(\d{4})$/);
                                if (m) {
                                    const month = MONTH_MAP[m[1].toLowerCase()];
                                    const year = parseInt(m[2]);
                                    if (month !== undefined && !isNaN(year)) {
                                        return dayjs().year(year).month(month).date(1).startOf('day').valueOf();
                                    }
                                }
                            }

                            // Format: "MM/YYYY" — e.g. "03/2024"
                            if (fmt === 'MM/YYYY') {
                                const m = s.match(/^(\d{1,2})\/(\d{4})$/);
                                if (m) return dayjs().year(parseInt(m[2])).month(parseInt(m[1]) - 1).date(1).startOf('day').valueOf();
                            }

                            // Format: "YYYY-MM" or "YYYY-MM-DD" — ISO-style
                            if (fmt.startsWith('YYYY-MM')) {
                                const d = dayjs(s);
                                if (d.isValid()) return d.valueOf();
                            }

                            // Last resort: native parse (works for standard ISO strings)
                            const fallback = dayjs(s);
                            return fallback.isValid() ? fallback.valueOf() : 0;
                        };

                        uniqueValues.sort((a, b) => parseDate(String(b)) - parseDate(String(a))); // Descending (Newest First)
                    }


                    setOptions(uniqueValues);

                    // --- AUTO SELECT LATEST ---
                    if (useLatest && uniqueValues.length > 0 && !selectedValue && (!selectedValues || selectedValues.length === 0)) {
                        const latest = uniqueValues[0]; // Top row based on SortBy DESC
                        onFilterChange(fieldName, selectionMode === 'multi' ? [latest] : latest, selectionMode);
                        // Signal that this useLatest filter has resolved
                        onFiltersResolved?.();
                    } else if (useLatest) {
                        // Already has a value — still resolved, signal the gate
                        onFiltersResolved?.();
                    }
                } else { setOptions([]); }
            } catch (err) {
                console.error("[DropdownFilter] Error:", err);
                setOptions([]);
                // CRITICAL: Even on error, signal that this filter has "resolved" (failed but done)
                // so the filtersReady gate is not permanently blocked.
                if (useLatest) onFiltersResolved?.();
            }
            finally { setLoading(false); }
        };
        fetchDistinct();
    }, [fieldName, dataset?.id, dataModel?.id, sortBy]); // Added sortBy dependency

    const filteredOptions = options.filter(opt => String(opt).toLowerCase().includes(searchQuery.toLowerCase()));

    const handleSelect = (opt) => {
        if (selectionMode === 'multi') {
            const currentValues = selectedValues || [];
            const optStr = String(opt);
            const isIncluded = currentValues.some(v => String(v) === optStr);
            const newValues = isIncluded ? currentValues.filter(v => String(v) !== optStr) : [...currentValues, opt];
            onFilterChange(fieldName, newValues.length > 0 ? newValues : null, 'multi');
        } else {
            const isSelected = String(selectedValue) === String(opt);
            onFilterChange(fieldName, isSelected ? null : opt, 'single');
            setIsOpen(false);
            setSearchQuery('');
        }
    };

    const displayValue = selectionMode === 'multi'
        ? (selectedValues?.length > 0 ? `${selectedValues.length} selected` : 'All')
        : (selectedValue || 'All');

    const showSearch = searchEnabled && options.length > 6;


    const containerWidth = width > 0 ? `${width}px` : '100%';
    const containerHeight = height > 0 ? `${height}px` : '100%';

    return (
        <div style={{ width: containerWidth, height: containerHeight, fontSize: `${fontSize}px` }} className="flex flex-col">
            <button
                ref={triggerRef}
                onClick={() => setIsOpen(!isOpen)}
                disabled={loading}
                className={`flex-1 flex items-center justify-between px-2.5 py-1.5 rounded-md border transition-all cursor-pointer text-left
                    ${loading ? 'bg-slate-100 border-slate-200' : ''}
                    ${isOpen ? 'border-blue-400 ring-1 ring-blue-100 bg-white' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
            >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" /> :
                    <span className={`truncate ${selectedValue || selectedValues?.length ? 'text-slate-800' : 'text-slate-400'}`}>{displayValue}</span>}
                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && ReactDOM.createPortal(
                <>
                    <div className="fixed inset-0" style={{ zIndex: 9998 }} onClick={() => { setIsOpen(false); setSearchQuery(''); }} />
                    <div className="fixed bg-white rounded-lg shadow-2xl border border-slate-200 overflow-hidden" style={{ zIndex: 9999, top: `${dropdownPos.top}px`, left: `${dropdownPos.left}px`, width: `${dropdownPos.width}px`, maxHeight: '240px' }}>
                        {showSearch && (
                            <div className="p-1.5 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
                                <div className="relative">
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                                    <input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                                        style={{ fontSize: `${fontSize}px` }}
                                        className="w-full pl-8 pr-2 py-1.5 bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400"
                                        autoFocus onClick={(e) => e.stopPropagation()} />
                                </div>
                            </div>
                        )}
                        <div className="overflow-y-auto" style={{ maxHeight: '180px' }}>
                            <button onClick={() => { onFilterChange(fieldName, null); setIsOpen(false); }}
                                style={{ fontSize: `${fontSize}px` }}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors
                                    ${!selectedValue && (!selectedValues || selectedValues.length === 0) ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' : 'hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                                <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${!selectedValue ? 'border-blue-500 bg-blue-500' : 'border-slate-300'}`}>
                                    {!selectedValue && <Check className="w-2 h-2 text-white" />}
                                </div>
                                <span>All Values</span>
                            </button>
                            {filteredOptions.map((opt, idx) => {
                                const optStr = String(opt);
                                const isSelected = selectionMode === 'multi'
                                    ? selectedValues?.some(v => String(v) === optStr)
                                    : String(selectedValue) === optStr;

                                return (
                                    <button key={idx} onClick={() => handleSelect(opt)}
                                        style={{ fontSize: `${fontSize}px` }}
                                        className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors
                                            ${isSelected ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' : 'hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'}`}>
                                        <div className={`w-3.5 h-3.5 ${selectionMode === 'multi' ? 'rounded' : 'rounded-full'} border-2 flex items-center justify-center shrink-0
                                            ${isSelected ? 'border-blue-500 bg-blue-500' : 'border-slate-300'}`}>
                                            {isSelected && <Check className="w-2 h-2 text-white" />}
                                        </div>
                                        <span className="truncate">{optStr}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </>,
                document.body
            )}
        </div>
    );
};

// ==================== TOGGLE FILTER ====================
const ToggleFilter = ({
    fieldName,
    options = [], // Two toggle options
    selectedValue,
    onFilterChange,
    width = 200,
    height = 32,
    fontSize = 12,
    label = fieldName,
    isEditMode = false
}) => {
    const opt1 = options[0] || 'Option 1';
    const opt2 = options[1] || 'Option 2';


    const isSecond = selectedValue === opt2;
    const containerWidth = width > 0 ? `${width}px` : '100%';
    const containerHeight = height > 0 ? `${height}px` : '100%';

    return (
        <div style={{ width: containerWidth, height: containerHeight, fontSize: `${fontSize}px` }} className="flex flex-col">
            <div className="flex-1 flex items-center bg-slate-100 rounded-lg p-1 gap-1">
                <button
                    onClick={() => onFilterChange(fieldName, opt1)}
                    className={`flex-1 py-1 rounded-md transition-all font-medium truncate px-1
                        ${!isSecond ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                >
                    {opt1}
                </button>
                <button
                    onClick={() => onFilterChange(fieldName, opt2)}
                    className={`flex-1 py-1 rounded-md transition-all font-medium truncate px-1
                        ${isSecond ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                >
                    {opt2}
                </button>
            </div>
        </div>
    );
};

// ==================== RANGE SLIDER FILTER ====================
const RangeFilter = ({
    fieldName,
    dataset,
    dataModel,
    minValue,
    maxValue,
    onFilterChange,
    width = 200,
    height = 32,
    fontSize = 12,
    dashboardId,
    widgetId,
    label = fieldName,
    isEditMode = false
}) => {
    const [range, setRange] = useState({ min: null, max: null });
    const [bounds, setBounds] = useState({ min: 0, max: 100 });
    const [loading, setLoading] = useState(false);

    // Fetch min/max from dataset
    useEffect(() => {
        if (!fieldName || (!dataset?.id && !dataModel?.id)) return;

        const fetchBounds = async () => {
            setLoading(true);
            try {
                const payload = {
                    dataset_id: dataModel?.id ? undefined : (dataset?.id || undefined),
                    data_model_id: dataModel?.id || undefined,
                    dashboard_id: dashboardId,
                    limit: 1,
                    x_field: fieldName,
                    y_series: [
                        { id: 'min_val', field: fieldName, agg: 'min', label: 'Min' },
                        { id: 'max_val', field: fieldName, agg: 'max', label: 'Max' }
                    ],
                    columns: null, filters: [], sort_by: null, sort_dir: null,
                    group_field: null, rank_by: null
                };
                
                const uniqueWidgetId = widgetId ? `${widgetId}_range_${fieldName}` : null;
                const result = await queryBatcher.fetch(dashboardId, uniqueWidgetId, payload);
                if (result?.data && result.data[0]) {
                    const row = result.data[0];
                    setBounds({ min: row.min_val || 0, max: row.max_val || 100 });
                    setRange({ min: row.min_val || 0, max: row.max_val || 100 });
                }
            } catch (err) { console.error("[RangeFilter] Error:", err); }
            finally { setLoading(false); }
        };
        fetchBounds();
    }, [fieldName, dataset?.id]);

    // Sync from external values
    useEffect(() => {
        if (minValue !== undefined) setRange(prev => ({ ...prev, min: minValue }));
        if (maxValue !== undefined) setRange(prev => ({ ...prev, max: maxValue }));
    }, [minValue, maxValue]);

    const handleMinChange = (e) => {
        const val = Number(e.target.value);
        setRange(prev => ({ ...prev, min: val }));
    };

    const handleMaxChange = (e) => {
        const val = Number(e.target.value);
        setRange(prev => ({ ...prev, max: val }));
    };

    const handleApply = () => {
        onFilterChange(fieldName, { min: range.min, max: range.max }, 'range');
    };

    const containerWidth = width > 0 ? `${width}px` : '100%';
    const containerHeight = height > 0 ? `${height}px` : '100%';

    return (
        <div style={{ width: containerWidth, height: containerHeight, fontSize: `${fontSize}px` }} className="flex flex-col">
            {loading ? (
                <div className="flex-1 flex items-center justify-center"><Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" /></div>
            ) : (
                <div className="flex-1 flex items-center gap-1">
                    <input
                        type="number"
                        value={range.min ?? bounds.min}
                        onChange={handleMinChange}
                        onBlur={handleApply}
                        className="w-16 p-1 border border-slate-200 rounded text-center"
                        style={{ fontSize: `${fontSize}px` }}
                        placeholder="Min"
                    />
                    <div className="flex-1 h-1 bg-slate-200 rounded relative">
                        <div
                            className="absolute h-full bg-blue-500 rounded"
                            style={{
                                left: `${((range.min - bounds.min) / (bounds.max - bounds.min)) * 100}%`,
                                right: `${100 - ((range.max - bounds.min) / (bounds.max - bounds.min)) * 100}%`
                            }}
                        />
                    </div>
                    <input
                        type="number"
                        value={range.max ?? bounds.max}
                        onChange={handleMaxChange}
                        onBlur={handleApply}
                        className="w-16 p-1 border border-slate-200 rounded text-center"
                        style={{ fontSize: `${fontSize}px` }}
                        placeholder="Max"
                    />
                </div>
            )}
        </div>
    );
};

// ==================== FILTER ROW (Wrapper with Settings) ====================
const FilterRow = ({
    field,
    dataset,
    dataModel,
    widget,
    globalFilters,
    onFilterChange,
    onRemove,
    onUpdateFieldConfig,
    isEditMode,
    dashboardId,
    onFiltersResolved = null,
}) => {
    const { name: fieldName, label = '', displayMode = 'dropdown', toggleOptions = [], searchEnabled = true, selectionMode = 'single', width = 0, height = 32, fontSize = 12 } = field;
    const displayLabel = label || fieldName;
    const [showSettings, setShowSettings] = useState(false);
    const settingsRef = useRef(null);

    const selectedValue = globalFilters?.[fieldName];
    const selectedValues = Array.isArray(selectedValue) ? selectedValue : [];

    return (
        <div
            className="flex flex-col relative group p-2 bg-slate-50/50 dark:bg-slate-800/50 rounded-lg border border-slate-100 dark:border-slate-700 hover:border-slate-200 dark:hover:border-slate-600 transition-colors"
            style={{ width: width === 0 ? '100%' : 'auto' }}
        >
            {/* Header: Label + Actions */}
            <div className="flex items-center justify-between mb-1 min-h-[16px]">
                <div className="text-[10px] text-slate-500 font-medium truncate pr-2 flex-1" title={displayLabel}>
                    {displayLabel}
                </div>
                {isEditMode && (
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                        <button
                            ref={settingsRef}
                            onClick={() => setShowSettings(!showSettings)}
                            className="p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors"
                            title="Filter Settings"
                        >
                            <Settings className="w-3.5 h-3.5" />
                        </button>
                        {(selectedValue || selectedValues.length > 0) && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onFilterChange(fieldName, null); }}
                                className="px-1.5 py-0.5 mx-0.5 rounded text-[10px] font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors"
                                title="Clear Selected Values"
                            >
                                Clear
                            </button>
                        )}
                        <button
                            onClick={(e) => { e.stopPropagation(); onRemove(); }}
                            className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                            title="Remove Filter Field"
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    </div>
                )}
            </div>

            {/* Render Based on Display Mode */}
            {displayMode === 'dropdown' && (
                <DropdownFilter
                    fieldName={fieldName}
                    label={displayLabel}
                    dataset={dataset}
                    dataModel={dataModel}
                    selectedValue={selectionMode === 'single' ? selectedValue : null}
                    selectedValues={selectionMode === 'multi' ? selectedValues : []}
                    onFilterChange={onFilterChange}
                    searchEnabled={searchEnabled}
                    selectionMode={selectionMode}
                    width={width}
                    height={height}
                    fontSize={fontSize}
                    dashboardId={dashboardId}
                    widgetId={widget.id}
                    useLatest={field.useLatest}
                    sortBy={field.sortBy}
                    isDate={field.isDate}
                    dateFormat={field.dateFormat}
                    onFiltersResolved={onFiltersResolved}
                    isEditMode={isEditMode}
                />
            )}
            {displayMode === 'toggle' && (
                <ToggleFilter
                    fieldName={fieldName}
                    label={displayLabel}
                    options={toggleOptions}
                    selectedValue={selectedValue}
                    onFilterChange={onFilterChange}
                    width={width}
                    height={height}
                    fontSize={fontSize}
                    isEditMode={isEditMode}
                />
            )}
            {displayMode === 'range' && (
                <RangeFilter
                    fieldName={fieldName}
                    label={displayLabel}
                    dataset={dataset}
                    dataModel={dataModel}
                    minValue={selectedValue?.min}
                    maxValue={selectedValue?.max}
                    onFilterChange={onFilterChange}
                    width={width}
                    height={height}
                    fontSize={fontSize}
                    dashboardId={dashboardId}
                    widgetId={widget.id}
                    isEditMode={isEditMode}
                />
            )}

            {/* Settings Popup */}
            {showSettings && ReactDOM.createPortal(
                <>
                    <div className="fixed inset-0" style={{ zIndex: 9998 }} onClick={() => setShowSettings(false)} />
                    <div
                        className="fixed bg-white rounded-lg shadow-2xl border border-slate-200 p-3 space-y-2"
                        style={{
                            zIndex: 9999,
                            top: settingsRef.current ? settingsRef.current.getBoundingClientRect().bottom + 8 : 100,
                            left: settingsRef.current ? settingsRef.current.getBoundingClientRect().left - 150 : 100,
                            width: '180px'
                        }}
                    >
                        <div className="text-xs font-semibold text-slate-700 border-b pb-1 mb-2">Settings</div>

                        <div>
                            <label className="block text-[10px] text-slate-500 mb-1">Display Label</label>
                            <input
                                type="text"
                                placeholder={fieldName}
                                value={label || ''}
                                onChange={(e) => onUpdateFieldConfig(fieldName, { label: e.target.value })}
                                className="w-full text-xs p-1.5 border border-slate-200 rounded"
                            />
                        </div>

                        <div>
                            <label className="block text-[10px] text-slate-500 mb-1 mt-2">Display Mode</label>
                            <select
                                value={displayMode}
                                onChange={(e) => onUpdateFieldConfig(fieldName, { displayMode: e.target.value })}
                                className="w-full text-xs p-1.5 border border-slate-200 rounded"
                            >
                                <option value="dropdown">Dropdown</option>
                                <option value="toggle">Toggle</option>
                                <option value="range">Range Slider</option>
                            </select>
                        </div>

                        {displayMode === 'dropdown' && (
                            <>
                                <div>
                                    <label className="block text-[10px] text-slate-500 mb-1">Selection</label>
                                    <select
                                        value={selectionMode}
                                        onChange={(e) => onUpdateFieldConfig(fieldName, { selectionMode: e.target.value })}
                                        className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                    >
                                        <option value="single">Single Select</option>
                                        <option value="multi">Multi Select</option>
                                    </select>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] text-slate-500">Search</span>
                                    <input
                                        type="checkbox"
                                        checked={searchEnabled}
                                        onChange={(e) => onUpdateFieldConfig(fieldName, { searchEnabled: e.target.checked })}
                                        className="w-4 h-4"
                                    />
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] text-slate-500">Default to Latest</span>
                                    <input
                                        type="checkbox"
                                        checked={!!field.useLatest}
                                        onChange={(e) => onUpdateFieldConfig(fieldName, { useLatest: e.target.checked })}
                                        className="w-4 h-4"
                                    />
                                </div>
                                {field.useLatest && (
                                    <div>
                                        <label className="block text-[10px] text-slate-500 mb-1">Sort By Column (for Chronological Latest)</label>
                                        <select
                                            value={field.sortBy || fieldName}
                                            onChange={(e) => onUpdateFieldConfig(fieldName, { sortBy: e.target.value })}
                                            className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                        >
                                            <option value={fieldName}>Self ({fieldName})</option>
                                            {(dataset?.dimensions || []).map((d: any) => d.name !== fieldName && (
                                                <option key={d.name} value={d.name}>{d.name} (Dim)</option>
                                            ))}
                                            {(dataset?.measures || []).map((m: any) => (
                                                <option key={m.name} value={m.name}>{m.name} (Msr)</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Date Settings for Sorting */}
                        {displayMode === 'dropdown' && (
                            <div className="pt-2 border-t mt-2">
                                <label className="flex items-center gap-2 mb-1 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={!!field.isDate}
                                        onChange={(e) => onUpdateFieldConfig(fieldName, { isDate: e.target.checked })}
                                        className="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-[10px] font-medium text-slate-700">Treat as Date (Sort Desc)</span>
                                </label>
                                {field.isDate && (
                                    <div className="ml-5">
                                        <input
                                            type="text"
                                            placeholder="Format (e.g. MMM YYYY)"
                                            value={field.dateFormat || ''}
                                            onChange={(e) => onUpdateFieldConfig(fieldName, { dateFormat: e.target.value })}
                                            className="w-full text-[10px] p-1.5 border border-slate-200 rounded"
                                        />
                                        <div className="text-[9px] text-slate-400 mt-0.5">Sorts recent to oldest</div>
                                    </div>
                                )}
                            </div>
                        )}

                        {displayMode === 'toggle' && (
                            <div>
                                <label className="block text-[10px] text-slate-500 mb-1">Toggle Options (comma-separated)</label>
                                <input
                                    type="text"
                                    placeholder="Option1, Option2"
                                    defaultValue={toggleOptions.join(', ')}
                                    onBlur={(e) => onUpdateFieldConfig(fieldName, { toggleOptions: e.target.value.split(',').map(s => s.trim()) })}
                                    className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                />
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-2 pt-1 border-t mt-2">
                            <div className="col-span-2">
                                <label className="block text-[10px] text-slate-500 mb-1">Default Value</label>
                                {displayMode === 'range' ? (
                                    <div className="flex gap-1">
                                        <input
                                            type="number"
                                            placeholder="Min"
                                            defaultValue={field.defaultValue?.min}
                                            onBlur={(e) => onUpdateFieldConfig(fieldName, { defaultValue: { ...field.defaultValue, min: Number(e.target.value) || null } })}
                                            className="w-full text-xs p-1 border border-slate-200 rounded"
                                        />
                                        <input
                                            type="number"
                                            placeholder="Max"
                                            defaultValue={field.defaultValue?.max}
                                            onBlur={(e) => onUpdateFieldConfig(fieldName, { defaultValue: { ...field.defaultValue, max: Number(e.target.value) || null } })}
                                            className="w-full text-xs p-1 border border-slate-200 rounded"
                                        />
                                    </div>
                                ) : (
                                    <input
                                        type="text"
                                        placeholder={selectionMode === 'multi' ? "Val1, Val2" : "Select default..."}
                                        defaultValue={Array.isArray(field.defaultValue) ? field.defaultValue.join(', ') : field.defaultValue || ''}
                                        onBlur={(e) => {
                                            const val = e.target.value;
                                            const finalVal = selectionMode === 'multi' ? val.split(',').map(s => s.trim()).filter(Boolean) : val;
                                            onUpdateFieldConfig(fieldName, { defaultValue: finalVal || null });
                                        }}
                                        className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                    />
                                )}
                            </div>
                            <div>
                                <label className="block text-[10px] text-slate-500 mb-1">Width (0=Auto)</label>
                                <input
                                    type="number" min={80} max={400}
                                    value={width}
                                    onChange={(e) => onUpdateFieldConfig(fieldName, { width: Number(e.target.value) })}
                                    className="w-full text-xs p-1 border border-slate-200 rounded"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] text-slate-500 mb-1">Height (0=Auto)</label>
                                <input
                                    type="number" min={24} max={150}
                                    value={height}
                                    onChange={(e) => onUpdateFieldConfig(fieldName, { height: Number(e.target.value) })}
                                    className="w-full text-xs p-1 border border-slate-200 rounded"
                                />
                            </div>
                            <div className="col-span-2">
                                <label className="block text-[10px] text-slate-500 mb-1">Text Size (px)</label>
                                <input
                                    type="number" min={8} max={36}
                                    value={fontSize}
                                    onChange={(e) => onUpdateFieldConfig(fieldName, { fontSize: Number(e.target.value) })}
                                    className="w-full text-xs p-1 border border-slate-200 rounded"
                                />
                            </div>
                        </div>
                    </div >
                </>,
                document.body
            )
            }
        </div >
    );
};

// ==================== MAIN FILTER WIDGET ====================
export const FilterWidget = ({ widget, dataset, dataModel, globalFilters, onFilterChange, onFiltersResolved = null, onWidgetUpdate, isEditMode }) => {
    const { config } = widget;
    const filterFields = config.column || [];
    const [fieldToDelete, setFieldToDelete] = useState(null);

    const bgColor = config?.style?.filter?.backgroundColor || 'transparent';
    const borderColor = config?.style?.filter?.borderColor || '#e2e8f0';
    const borderRadius = config?.style?.filter?.borderRadius || 8;
    const marginTop = config?.style?.filter?.marginTop || 0;
    const marginLeft = config?.style?.filter?.marginLeft || 0;
    const showBorder = config?.style?.filter?.showBorder !== false;

    const handleRemoveField = (fieldNameToRemove) => {
        // Clear the actual filter value
        onFilterChange(fieldNameToRemove, null);

        // Remove the field from the widget's config
        const newColumns = filterFields.filter(f => f.name !== fieldNameToRemove);
        onWidgetUpdate?.(widget.id, { config: { ...config, column: newColumns } });
    };

    // Update a specific field's configuration
    const handleUpdateFieldConfig = (fieldName, updates) => {
        const newColumns = filterFields.map(f =>
            f.name === fieldName ? { ...f, ...updates } : f
        );
        onWidgetUpdate?.(widget.id, { config: { ...config, column: newColumns } });
    };

    // Empty State
    if (filterFields.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-slate-400 text-xs p-4"
                style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderRadius: `${borderRadius}px`, border: '2px dashed #cbd5e1' }}>
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mb-2">
                    <Filter className="w-5 h-5 text-slate-400" />
                </div>
                <span className="font-medium text-slate-500">Drop fields here</span>
                <span className="text-[10px] text-slate-400 mt-0.5">to create filters</span>
            </div>
        );
    }

    return (
        <div className="h-full w-full p-2 flex flex-wrap gap-2 content-start items-start overflow-y-auto"
            style={{
                background: bgColor,
                borderRadius: `${borderRadius}px`,
                border: showBorder ? `1px solid ${borderColor}` : 'none',
                marginTop: `${marginTop}px`,
                marginLeft: `${marginLeft}px`,
                height: `calc(100% - ${marginTop}px)`,
                width: `calc(100% - ${marginLeft}px)`
            }}>
            {filterFields.map((field, idx) => (
                <FilterRow
                    key={field.name || idx}
                    field={field}
                    dataset={dataset}
                    dataModel={dataModel}
                    widget={widget}
                    globalFilters={globalFilters}
                    onFilterChange={onFilterChange}
                    onRemove={() => setFieldToDelete(field.name)}
                    onUpdateFieldConfig={handleUpdateFieldConfig}
                    isEditMode={isEditMode}
                    dashboardId={widget.dashboardId || widget.dashboard_id || (typeof window !== 'undefined' ? window.location.pathname.split('/').pop() : null)}
                    onFiltersResolved={onFiltersResolved}
                />
            ))}

            <ConfirmModal
                isOpen={!!fieldToDelete}
                title="Delete Filter Field"
                message={`Are you sure you want to delete the filter for '${filterFields.find(f => f.name === fieldToDelete)?.label || fieldToDelete}'? This action cannot be undone and it will not be retrieved again.`}
                confirmText="Delete"
                isDestructive={true}
                onConfirm={() => {
                    handleRemoveField(fieldToDelete);
                    setFieldToDelete(null);
                }}
                onCancel={() => setFieldToDelete(null)}
            />
        </div>
    );
};
